/**
 * AppSidebar Component
 *
 * Application sidebar using shadcn sidebar-07 pattern
 * with collapsible navigation and user profile
 */

import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    Plane,
    Settings,
    LayoutDashboard,
    PlaneLanding,
    ListChecks,
    Cog,
    UserRound,
    SquareLibrary,
    MonitorCog,
    History,
    ClipboardCopy,
    MapPin,
    ChevronRight,
} from 'lucide-react';

import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarRail,
} from '@/components/ui/sidebar';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';


// Navigation data
const navigationData = {
    mainNav: [
        {
            title: 'Dashboard',
            url: '/dashboard',
            icon: LayoutDashboard,
        },
        {
            title: 'New Flight',
            url: '/new-flight',
            icon: PlaneLanding,
        },
        {
            title: 'Live Operations',
            url: '/live-operation',
            icon: MonitorCog,
        },
    ],
    assetManagement: {
        title: 'Asset Management',
        icon: ListChecks,
        items: [
            { title: 'Gear', url: '/asset-management/gear', icon: Cog },
            { title: 'Pilot', url: '/asset-management/pilot', icon: UserRound },
            { title: 'Items', url: '/asset-management/items', icon: SquareLibrary },
        ],
    },
    missionPlanning: {
        title: 'Mission Planning',
        icon: Plane,
        items: [
            { title: 'Missions', url: '/mission', icon: ClipboardCopy },
            { title: 'Area of Interest', url: '/mission-planning/area-of-interest', icon: MapPin },
        ],
    },
    bottomNav: [
        {
            title: 'Mission History',
            url: '/mission-history',
            icon: History,
        },
        {
            title: 'Settings',
            url: '/settings',
            icon: Settings,
        },
    ],
    user: {
        name: 'Commander',
        email: 'commander@dronemp.id',
        avatar: 'CM',
    },
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
    const location = useLocation();

    const isActive = (url: string) => location.pathname === url;
    const isGroupActive = (items: { url: string }[]) =>
        items.some((item) => location.pathname.startsWith(item.url));

    return (
        <Sidebar collapsible="icon" className="bg-slate-900 text-white" {...props}>
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton size="lg" asChild>
                            <NavLink to="/">
                                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-blue-600 text-white">
                                    <Plane className="size-4" />
                                </div>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-semibold">KOPASGAT</span>
                                    <span className="truncate text-xs text-sidebar-foreground/70">Drone Planner</span>
                                </div>
                            </NavLink>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>

            <SidebarContent>
                {/* All Navigation Items */}
                <SidebarGroup>
                    <SidebarMenu>
                        {/* Main Nav Items */}
                        {navigationData.mainNav.map((item) => (
                            <SidebarMenuItem key={item.title}>
                                <SidebarMenuButton 
                                    asChild 
                                    isActive={isActive(item.url)} 
                                    tooltip={item.title}
                                    className={isActive(item.url) ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                                >
                                    <NavLink to={item.url}>
                                        <item.icon />
                                        <span>{item.title}</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        ))}

                        {/* Asset Management */}
                        <Collapsible
                            asChild
                            defaultOpen={isGroupActive(navigationData.assetManagement.items)}
                            className="group/collapsible"
                        >
                            <SidebarMenuItem>
                                <CollapsibleTrigger asChild>
                                    <SidebarMenuButton tooltip={navigationData.assetManagement.title}>
                                        <navigationData.assetManagement.icon />
                                        <span>{navigationData.assetManagement.title}</span>
                                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                    </SidebarMenuButton>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <SidebarMenuSub>
                                        {navigationData.assetManagement.items.map((subItem) => (
                                            <SidebarMenuSubItem key={subItem.title}>
                                                <SidebarMenuSubButton 
                                                    asChild 
                                                    isActive={isActive(subItem.url)}
                                                    className={isActive(subItem.url) ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                                                >
                                                    <NavLink to={subItem.url}>
                                                        <subItem.icon className="size-4" />
                                                        <span>{subItem.title}</span>
                                                    </NavLink>
                                                </SidebarMenuSubButton>
                                            </SidebarMenuSubItem>
                                        ))}
                                    </SidebarMenuSub>
                                </CollapsibleContent>
                            </SidebarMenuItem>
                        </Collapsible>

                        {/* Mission Planning */}
                        <Collapsible
                            asChild
                            defaultOpen={isGroupActive(navigationData.missionPlanning.items)}
                            className="group/collapsible"
                        >
                            <SidebarMenuItem>
                                <CollapsibleTrigger asChild>
                                    <SidebarMenuButton tooltip={navigationData.missionPlanning.title}>
                                        <navigationData.missionPlanning.icon />
                                        <span>{navigationData.missionPlanning.title}</span>
                                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                    </SidebarMenuButton>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <SidebarMenuSub>
                                        {navigationData.missionPlanning.items.map((subItem) => (
                                            <SidebarMenuSubItem key={subItem.title}>
                                                <SidebarMenuSubButton 
                                                    asChild 
                                                    isActive={isActive(subItem.url)}
                                                    className={isActive(subItem.url) ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                                                >
                                                    <NavLink to={subItem.url}>
                                                        <subItem.icon className="size-4" />
                                                        <span>{subItem.title}</span>
                                                    </NavLink>
                                                </SidebarMenuSubButton>
                                            </SidebarMenuSubItem>
                                        ))}
                                    </SidebarMenuSub>
                                </CollapsibleContent>
                            </SidebarMenuItem>
                        </Collapsible>

                        {/* Bottom Nav Items */}
                        {navigationData.bottomNav.map((item) => (
                            <SidebarMenuItem key={item.title}>
                                <SidebarMenuButton 
                                    asChild 
                                    isActive={isActive(item.url)} 
                                    tooltip={item.title}
                                    className={isActive(item.url) ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                                >
                                    <NavLink to={item.url}>
                                        <item.icon />
                                        <span>{item.title}</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        ))}
                    </SidebarMenu>
                </SidebarGroup>
            </SidebarContent>

            <SidebarRail />
        </Sidebar>
    );
}
